import AuthBase from "../AuthBase";
import useCallDataApi from "../../hooks/data";
import {useContext, useEffect, useRef, useState} from "react";
import Loading from "../../components/Loading";
import {Button, Grid, IconButton, Stack, Typography, useTheme} from "@mui/material";
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DinoDialog from "../../components/Dialog";
import {useSnackbar} from "notistack";
import PopupData from "./components/PopupData";
import AuthContext from "../../context/AuthContext";
import {hasPermission} from "../../permissions/hasPermission";

const PopUp = () => {
    const {user} = useContext(AuthContext)
    const canCreatePopup = hasPermission(user, 'popup', 'create')
    const canEditPopup = hasPermission(user, 'popup', 'edit')
    const canDeletePopup = hasPermission(user, 'popup', 'delete')
    const [popups, setPopups] = useState([])
    const [loading, setLoading] = useState(false)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedPopup, setSelectedPopup] = useState(null)
    const {getData, updateData, deleteData, createData} = useCallDataApi('popups')
    const {enqueueSnackbar} = useSnackbar()
    const popupDataRef = useRef(null)

    useEffect(() => {
        setLoading(true)
        getData().then((data) => setPopups(data)).finally(() => setLoading(false))
    }, [])

    const handleEdit = (popup) => {
        setSelectedPopup(popup)
        setEditDialogOpen(true)
    }

    const handleDelete = (popup) => {
        setSelectedPopup(popup)
        setDeleteDialogOpen(true)
    }

    const handleNew = () => {
        setSelectedPopup(null)
        setEditDialogOpen(true)
    }

    const handleSave = () => {
        const data = popupDataRef.current?.getData()
        if (!data) return

        setLoading(true)
        const savePromise = selectedPopup 
            ? updateData(selectedPopup.id, data)
            : createData(data)

        savePromise
            .then(() => {
                getData().then((data) => setPopups(data))
                enqueueSnackbar(selectedPopup ? 'Sikeres mentés' : 'Sikeres létrehozás', {variant: 'success'})
            })
            .finally(() => {
                setLoading(false)
                setEditDialogOpen(false)
            })
    }

    const handleDeleteConfirm = () => {
        setLoading(true)
        deleteData(selectedPopup.id)
            .then(() => {
                getData().then((data) => setPopups(data))
                enqueueSnackbar('Sikeres törlés', {variant: 'success'})
            })
            .finally(() => {
                setLoading(false)
                setDeleteDialogOpen(false)
            })
    }

    const editActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setEditDialogOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={handleSave}>{selectedPopup ? 'Mentés' : 'Létrehozás'}</Button>
    </>

    const deleteActions = <>
        <Button variant='outlined' style={{padding: 15}} onClick={() => setDeleteDialogOpen(false)}>Mégsem</Button>
        <Button variant='contained' style={{padding: 15}} onClick={handleDeleteConfirm} color="error">Törlés</Button>
    </>

    const buttons = [{
        name: 'Új popup',
        props: {
            startIcon: <AddRoundedIcon/>,
            variant: 'contained',
            disabled: !canCreatePopup,
            onClick: handleNew
        }
    }]

    return <AuthBase label='Popupok' buttons={buttons}>
        <div className="main">
            <Grid container spacing={2}>
                {popups?.map((popup) => (
                    <Grid item xs={12} key={popup.id}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{
                            background: '#F3F5F7',
                            padding: 2,
                            borderRadius: '4px',
                            border: '1px solid',
                            borderColor: '#E2E8EB'
                        }}>
                            <Typography variant="subtitle1" sx={{flexGrow: 1}}>{popup.name}</Typography>
                            <IconButton disabled={!canEditPopup} onClick={() => handleEdit(popup)} color="primary">
                                <EditRoundedIcon/>
                            </IconButton>
                            <IconButton disabled={!canDeletePopup} onClick={() => handleDelete(popup)} color="error">
                                <DeleteRoundedIcon/>
                            </IconButton>
                        </Stack>
                    </Grid>
                ))}
            </Grid>
        </div>
        <Loading isLoading={loading}/>

        <DinoDialog open={editDialogOpen}
                    handleClose={() => setEditDialogOpen(false)}
                    title={selectedPopup ? 'Popup szerkesztése' : 'Új popup'}
                    subtitle={selectedPopup ? 'Módosítsa a popup adatait' : 'Adja meg az új popup adatait'}
                    actions={editActions}>
            <PopupData reference={popupDataRef} obj={selectedPopup} />
        </DinoDialog>

        <DinoDialog open={deleteDialogOpen}
                    handleClose={() => setDeleteDialogOpen(false)}
                    title='Popup törlése'
                    subtitle='Biztosan törli ezt a popupot?'
                    actions={deleteActions}>
            <Typography variant="body1">
                A popup törlése után nem lehet visszaállítani. Biztosan folytatja?
            </Typography>
        </DinoDialog>
    </AuthBase>
}

export default PopUp